import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AddFill, RightArrowBigIcon } from '../icon/icon';

interface CommonProps {
  title: string;
  createdAt: Date | string;
}

interface BoardProps extends CommonProps {
  noticeId: number;
}

interface InquiryListResponseData extends CommonProps {
  inquiryId: number;
  state: string; // 다른 타입의 속성
}

interface BoardItemsProps<T extends CommonProps> {
  items: T[];
  registBtn?: () => void;
  detailPathUrl?: string;
}

// 타입 가드 함수 정의
function isBoardProps(item: CommonProps): item is BoardProps {
  return 'noticeId' in item;
}

function isInquiryListResponseData(
  item: CommonProps,
): item is InquiryListResponseData {
  return 'inquiryId' in item && 'state' in item; // state 속성도 검사
}

export const CommonBoard = <T extends CommonProps>({
  items,
  registBtn,
  detailPathUrl,
}: BoardItemsProps<T>) => {
  return (
    <BoardWrap>
      <ul>
        {items.map((item, i) => (
          <li key={i}>
            <Link
              to={{
                pathname: `${detailPathUrl}/${
                  isBoardProps(item)
                    ? item.noticeId
                    : (item as unknown as InquiryListResponseData).inquiryId
                }`,
              }}
            >
              <div className="wrap">
                <div className="t-con">
                  <span className="board-title">{item.title}</span>
                </div>
                <div className="b-con">
                  <div className="l-con">
                    <span>{item.createdAt.toString()}</span>
                  </div>
                  {/* state가 있는 경우에만 조건부 렌더링 */}
                  {isInquiryListResponseData(item) && (
                    <div className="r-con">
                      <TypeBadge
                        className="type"
                        answerType={item.state === '답변 완료'}
                      >
                        {item.state === '답변 완료'
                          ? '답변 완료'
                          : '답변 미완료'}
                      </TypeBadge>
                    </div>
                  )}
                </div>
              </div>
              <div className="icon-box">
                <RightArrowBigIcon />
              </div>
            </Link>
          </li>
        ))}
      </ul>
      {registBtn ? (
        <div className="btn-box">
          <button onClick={registBtn}>
            <AddFill />
          </button>
        </div>
      ) : null}
    </BoardWrap>
  );
};

// style
const BoardWrap = styled.section`
  position: relative;
  ul {
    li {
      border-bottom: 1px solid #f0f0f0;
      a {
        padding: 2.4rem;
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: center;
        .wrap {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          .t-con {
            .board-title {
              font-size: 1.5rem;
              font-weight: 600;
            }
          }
          .b-con {
            display: flex;
            align-items: center;
            .l-con {
              font-size: 1.3rem;
              font-weight: 300;
              line-height: 1.8rem;
              color: ${(props) => props.theme.textAAAColor};
            }
            .r-con {
              font-size: 1.3rem;
              font-weight: 300;
              line-height: 1.8rem;
              margin-left: 2.1rem;
              position: relative;
              &::after {
                content: '';
                position: absolute;
                width: 1px;
                height: 1.2rem;
                background-color: ${(props) => props.theme.textAAAColor};
                top: 50%;
                transform: translateY(-50%);
                left: -1.1rem;
              }
            }
          }
        }
      }
    }
  }
  .btn-box {
    position: fixed;
    right: 1.5rem;
    bottom: 4.5rem;
    button {
      background-color: ${(props) => props.theme.btnBlueColor};
      width: 6.2rem;
      height: 6.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 20rem;
      box-shadow: 0 4px 5px 1px rgba(0, 0, 0, 0.1);
      border: none;
      outline: none;
    }
  }
`;

const TypeBadge = styled.span<{ answerType: boolean }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.answerType ? '#437BE6' : '#999999')};
`;
